import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";

import { eventUrl } from "@/lib/stripe-links";

const client = generateClient<Schema>();

export type TimelineActorType = "staff" | "merchant" | "stripe" | "system";

export interface TimelineEntry {
  id: string;
  occurredAt: string;
  source: string;
  kind: string;
  actorType: TimelineActorType;
  actorId: string | null;
  actorLabel: string | null;
  summary: string;
  detail: unknown;
  externalUrl: string | null;
  livemode: boolean | null;
}

export interface DeviceHistoryEntry {
  occurredAt: string;
  appVersion: string | null;
  buildNumber: string | null;
  platform: string | null;
  deviceModel: string | null;
  osName: string | null;
  osVersion: string | null;
}

export interface DeviceState {
  lastSeenAt: string | null;
  appVersion: string | null;
  buildNumber: string | null;
  platform: string | null;
  deviceModel: string | null;
  osName: string | null;
  osVersion: string | null;
  history: DeviceHistoryEntry[];
}

export interface ProjectedClaims {
  businessId: string | null;
  businessName: string | null;
  subscriptionStatus: string | null;
  plan: string | null;
}

export interface AuthState {
  userId: string | null;
  email: string | null;
  status: string | null;
  enabled: boolean | null;
  mfaEnabled: boolean | null;
  groups: string[];
  groupsUnavailable: boolean;
  createdAt: string | null;
  lastModifiedAt: string | null;
  unavailableReason: string | null;
}

export interface FlagResolution {
  key: string;
  serverVariant: string | null;
  /** Amplitude's configured payload, where the flag carries one. */
  serverPayload: string | null;
  deviceVariant: string | null;
}

export interface FlagState {
  flags: FlagResolution[];
  unavailable: boolean;
  deviceReportedAt: string | null;
}

export interface MerchantDebugContext {
  businessId: string;
  timeline: TimelineEntry[];
  authState: AuthState | null;
  projectedClaims: ProjectedClaims | null;
  deviceState: DeviceState | null;
  flagState: FlagState | null;
  partialSources: string[];
}

const ACTOR_TYPES: TimelineActorType[] = [
  "staff",
  "merchant",
  "stripe",
  "system",
];

function actorType(value: string | null | undefined): TimelineActorType {
  return ACTOR_TYPES.includes(value as TimelineActorType)
    ? (value as TimelineActorType)
    : "system";
}

const STRIPE_ID_PREFIX = "stripe:";

/**
 * The backend leaves `externalUrl` unset for Stripe entries and returns the
 * connected account as the actor instead, because every Stripe dashboard URL
 * in this app must be built through `stripe-links` — which resolves the
 * platform account for "view-as" links and only exists client-side.
 */
function withStripeUrl(
  entry: TimelineEntry,
  currency: string | null | undefined,
): TimelineEntry {
  if (entry.externalUrl || entry.source !== "stripe" || !entry.actorId) {
    return entry;
  }
  return {
    ...entry,
    externalUrl: eventUrl(entry.id.slice(STRIPE_ID_PREFIX.length), {
      source: "connected",
      account: entry.actorId,
      // Without this a test-mode event opens the live dashboard, which shows
      // "not found" rather than the event.
      livemode: entry.livemode,
      currency: currency ?? null,
    }),
  };
}

export async function fetchMerchantDebugContext(
  businessId: string,
  options?: { limit?: number; currency?: string | null },
): Promise<MerchantDebugContext> {
  const { data, errors } = await client.queries.adminGetMerchantDebugContext({
    businessId,
    limit: options?.limit,
  });

  if (errors?.length) {
    throw new Error(errors[0].message);
  }
  if (!data) {
    throw new Error("No debug context returned");
  }

  return {
    businessId: data.businessId,
    timeline: (data.timeline ?? []).flatMap((e) =>
      e
        ? [
            withStripeUrl(
              {
                id: e.id,
                occurredAt: e.occurredAt,
                source: e.source,
                kind: e.kind,
                actorType: actorType(e.actorType),
                actorId: e.actorId ?? null,
                actorLabel: e.actorLabel ?? null,
                summary: e.summary,
                detail: e.detail ?? null,
                externalUrl: e.externalUrl ?? null,
                livemode: e.livemode ?? null,
              },
              options?.currency,
            ),
          ]
        : [],
    ),
    authState: data.authState
      ? {
          userId: data.authState.userId ?? null,
          email: data.authState.email ?? null,
          status: data.authState.status ?? null,
          enabled: data.authState.enabled ?? null,
          mfaEnabled: data.authState.mfaEnabled ?? null,
          groups: (data.authState.groups ?? []).flatMap((g) => (g ? [g] : [])),
          groupsUnavailable: data.authState.groupsUnavailable ?? false,
          createdAt: data.authState.createdAt ?? null,
          lastModifiedAt: data.authState.lastModifiedAt ?? null,
          unavailableReason: data.authState.unavailableReason ?? null,
        }
      : null,
    projectedClaims: data.projectedClaims
      ? {
          businessId: data.projectedClaims.businessId ?? null,
          businessName: data.projectedClaims.businessName ?? null,
          subscriptionStatus: data.projectedClaims.subscriptionStatus ?? null,
          plan: data.projectedClaims.plan ?? null,
        }
      : null,
    deviceState: data.deviceState
      ? {
          lastSeenAt: data.deviceState.lastSeenAt ?? null,
          appVersion: data.deviceState.appVersion ?? null,
          buildNumber: data.deviceState.buildNumber ?? null,
          platform: data.deviceState.platform ?? null,
          deviceModel: data.deviceState.deviceModel ?? null,
          osName: data.deviceState.osName ?? null,
          osVersion: data.deviceState.osVersion ?? null,
          history: (data.deviceState.history ?? []).flatMap((h) =>
            h
              ? [
                  {
                    occurredAt: h.occurredAt,
                    appVersion: h.appVersion ?? null,
                    buildNumber: h.buildNumber ?? null,
                    platform: h.platform ?? null,
                    deviceModel: h.deviceModel ?? null,
                    osName: h.osName ?? null,
                    osVersion: h.osVersion ?? null,
                  },
                ]
              : [],
          ),
        }
      : null,
    flagState: data.flagState
      ? {
          flags: (data.flagState.flags ?? []).flatMap((f) =>
            f
              ? [
                  {
                    key: f.key,
                    serverVariant: f.serverVariant ?? null,
                    serverPayload: f.serverPayload ?? null,
                    deviceVariant: f.deviceVariant ?? null,
                  },
                ]
              : [],
          ),
          unavailable: data.flagState.unavailable,
          deviceReportedAt: data.flagState.deviceReportedAt ?? null,
        }
      : null,
    partialSources: (data.partialSources ?? []).flatMap((s) => (s ? [s] : [])),
  };
}
